import { useAuth } from '@agentnet/auth';
import { Grid, Typography } from '@material-ui/core';
import { OracleDowntimeType } from 'api/invoicing/oracle-downtime-api';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import useSnackBars from 'ui-kit/components/notification/useSnackbars';
import FormDrawerComponent from 'ui-kit/components/drawer/FormDrawerComponent';
import { doValidate, FieldValidationError } from 'utilities/validation/validation';
import { OracleDowntimeScheduleDisplay } from '../types';
import { createOracleDowntimeValidationSchema } from '../validation';
import {
  createOracleDowntimeSchedule,
  updateOracleDowntimeSchedule,
  CreateOracleDowntimeScheduleRequest,
  UpdateOracleDowntimeScheduleRequest,
} from 'api/invoicing/oracle-downtime-api';
import DateFieldString from 'ui-kit/inputs/DateField/DateFieldString';
import AgentNetDropdownSelector from 'ui-kit/inputs/AgentNetDropdownSelector';

interface ApiError {
  errorCode?: number;
  messages?: string[];
}

interface OracleDowntimeDrawerProps {
  open: boolean;
  mode: 'create' | 'edit';
  schedule?: OracleDowntimeScheduleDisplay;
  types: OracleDowntimeType[];
  onClose: () => void;
  onSuccess: () => void;
}

interface FormData {
  typeCdId: number | '';
  startDate: string;
  startTime: string;
  endDate: string;
  endTime: string;
}

// Generate 24-hour time options (12:00 AM through 11:00 PM)
const generateTimeOptions = (): Array<{ value: string; label: string }> => {
  const options: Array<{ value: string; label: string }> = [];
  for (let hour = 0; hour < 24; hour++) {
    const hour12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    const period = hour < 12 ? 'AM' : 'PM';
    const hourStr = String(hour).padStart(2, '0');
    options.push({
      value: `${hourStr}:00`,
      label: `${hour12}:00 ${period}`,
    });
  }
  return options;
};

// Determine if a date is in DST (PDT) or standard time (PST)
const getTimeZoneLabel = (dateStr: string): string => {
  if (!dateStr) return 'PST/PDT';

  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return 'PST/PDT';

  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Los_Angeles',
    timeZoneName: 'short',
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
  });

  const parts = formatter.formatToParts(date);
  const tzName = parts.find((p) => p.type === 'timeZoneName')?.value || '';
  return tzName.includes('PDT') ? 'PDT' : 'PST';
};

// Convert UTC Date to Pacific timezone date string (YYYY-MM-DD) and time string (HH:00)
const toDateAndTime = (date: Date | null): { dateStr: string; timeStr: string } => {
  if (!date) return { dateStr: '', timeStr: '' };

  const pacificString = date.toLocaleString('en-US', {
    timeZone: 'America/Los_Angeles',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });

  // Format: "MM/DD/YYYY, HH:mm"
  const [datePart, timePart] = pacificString.split(', ');
  const [month, day, year] = datePart.split('/');
  const [hour, minute] = timePart.split(':');

  return {
    dateStr: `${month}/${day}/${year}`, // MM/DD/YYYY format for DateFieldString
    timeStr: `${hour}:${minute}`,
  };
};

// Convert Pacific timezone date + time strings to UTC Date
const fromDateAndTime = (dateStr: string, timeStr: string): Date | null => {
  if (!dateStr || !timeStr) return null;

  // Parse MM/DD/YYYY date format
  const [month, day, year] = dateStr.split('/').map(Number);

  // Determine if DST is in effect for this date by checking timezone name
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Los_Angeles',
    timeZoneName: 'short',
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
  });

  const testForDST = new Date(Date.UTC(year, month - 1, day, 12, 0, 0));
  const parts = formatter.formatToParts(testForDST);
  const tzName = parts.find((p) => p.type === 'timeZoneName')?.value || '';
  const isDST = tzName.includes('PDT') || tzName.includes('D');

  // Construct ISO string with explicit offset: PST is -08:00, PDT is -07:00
  const offset = isDST ? '-07:00' : '-08:00';
  const yearStr = year.toString().padStart(4, '0');
  const monthStr = month.toString().padStart(2, '0');
  const dayStr = day.toString().padStart(2, '0');
  const isoString = `${yearStr}-${monthStr}-${dayStr}T${timeStr}:00.000${offset}`;

  return new Date(isoString);
};

const OracleDowntimeDrawer: React.FC<OracleDowntimeDrawerProps> = ({
  open,
  mode,
  schedule,
  types,
  onClose,
  onSuccess,
}) => {
  const { getAccessToken } = useAuth();
  const { addSnackbarMessage } = useSnackBars();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    typeCdId: '',
    startDate: '',
    startTime: '',
    endDate: '',
    endTime: '',
  });
  const [validationErrors, setValidationErrors] = useState<FieldValidationError[]>([]);

  // If schedule has started, can ONLY edit End Date (Type and Start Date/Time are locked)
  const scheduleHasStarted = useMemo(() => {
    if (mode !== 'edit' || !schedule) return false;
    return schedule.startDateTime.getTime() < new Date().getTime();
  }, [mode, schedule]);

  useEffect(() => {
    if (open) {
      if (mode === 'edit' && schedule) {
        const start = toDateAndTime(schedule.startDateTime);
        const end = toDateAndTime(schedule.endDateTime);
        setFormData({
          typeCdId: schedule.typeCdId,
          startDate: start.dateStr,
          startTime: start.timeStr,
          endDate: end.dateStr,
          endTime: end.timeStr,
        });
      } else {
        setFormData({
          typeCdId: '',
          startDate: '',
          startTime: '',
          endDate: '',
          endTime: '',
        });
      }
      setValidationErrors([]);
    }
  }, [open, mode, schedule]);

  const handleFieldChange = useCallback((field: keyof FormData, value: string) => {
    let processedValue: string | number = value;

    if (field === 'typeCdId') {
      processedValue = value ? Number(value) : '';
    }

    setFormData((prev) => ({ ...prev, [field]: processedValue }));

    // Clear validation errors for related fields
    if (field === 'startDate' || field === 'startTime') {
      setValidationErrors((prev) => prev.filter((e) => e.field !== 'startDateTime'));
    } else if (field === 'endDate' || field === 'endTime') {
      setValidationErrors((prev) => prev.filter((e) => e.field !== 'endDateTime'));
    } else {
      setValidationErrors((prev) => prev.filter((e) => e.field !== field));
    }
  }, []);

  const handleSubmit = useCallback(async () => {
    try {
      setLoading(true);

      const validationData = {
        type: formData.typeCdId,
        startDateTime: fromDateAndTime(formData.startDate, formData.startTime),
        endDateTime: fromDateAndTime(formData.endDate, formData.endTime),
      };

      const validationSchema = createOracleDowntimeValidationSchema(mode);
      const errors = await doValidate(validationData, validationSchema);
      if (errors) {
        setValidationErrors(errors);
        addSnackbarMessage({
          message: 'Please fix validation errors before submitting',
          type: 'error',
        });
        setLoading(false);
        return;
      }

      const token = await getAccessToken();

      // Validation guarantees startDateTime exists; non-null assertion is safe here
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const startDateTime = validationData.startDateTime!;

      if (mode === 'create') {
        const payload: CreateOracleDowntimeScheduleRequest = {
          DowntimeTypeCdId: formData.typeCdId as number,
          StartDateTime: startDateTime.toISOString(),
          EndDateTime: validationData.endDateTime?.toISOString() || null,
        };
        await createOracleDowntimeSchedule(payload, token);
        addSnackbarMessage({
          message: 'Oracle Downtime Schedule created successfully',
          type: 'success',
        });
      } else {
        if (!schedule) {
          throw new Error('Schedule is required for edit mode');
        }
        const payload: UpdateOracleDowntimeScheduleRequest = {
          OracleDowntimeScheduleId: schedule.id,
          DowntimeTypeCdId: formData.typeCdId as number,
          StartDateTime: startDateTime.toISOString(),
          EndDateTime: validationData.endDateTime?.toISOString() || null,
          UpdTS: schedule.updTS,
        };
        await updateOracleDowntimeSchedule(payload, token);
        addSnackbarMessage({
          message: 'Oracle Downtime Schedule updated successfully',
          type: 'success',
        });
      }

      setLoading(false);
      onSuccess();
    } catch (error) {
      setLoading(false);
      const apiError = error as ApiError;

      if (
        apiError.errorCode === 409 ||
        apiError.messages?.some((m: string) => m.toLowerCase().includes('concurrency'))
      ) {
        addSnackbarMessage({
          message: 'This schedule was modified by another user. The data will refresh with the latest version.',
          type: 'warning',
        });
        onSuccess();
      } else {
        addSnackbarMessage({
          message: apiError.messages?.join(', ') || 'Failed to save Oracle Downtime Schedule',
          type: 'error',
        });
      }
    }
  }, [formData, mode, schedule, getAccessToken, addSnackbarMessage, onSuccess]);

  const getFieldError = (fieldName: string): string | undefined => {
    const error = validationErrors.find((e) => e.field === fieldName);
    return error?.message;
  };

  return (
    <FormDrawerComponent
      testId="oracle-downtime-drawer"
      open={open}
      title={mode === 'create' ? 'Schedule Downtime' : 'Edit Downtime'}
      width={560}
      primaryActionLabel="Save Schedule"
      primaryActionDataQa="schedule-downtime-submit-button"
      dismissActionLabel="Cancel"
      dismissActionProps={{ 'data-testid': 'schedule-downtime-cancel-button' }}
      onDismissAction={onClose}
      onPrimaryAction={handleSubmit}
      primaryActionProps={{ loading: loading, disabled: loading }}
    >
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <AgentNetDropdownSelector
            qaAttribute="downtime-type-select"
            label="Type"
            name="type"
            value={formData.typeCdId}
            menuOption={(value) => handleFieldChange('typeCdId', value as string)}
            errs={validationErrors}
            showValidation={true}
            required
            isFieldDisable={scheduleHasStarted}
            hideEditable={true}
            options={types.map((t) => ({ name: t.Description, value: t.TypeCdId }))}
          />
          {scheduleHasStarted && (
            <Typography variant="caption" color="textSecondary" style={{ marginTop: 4 }}>
              Cannot modify Type for started schedules
            </Typography>
          )}
        </Grid>
        <Grid item xs={6}>
          <DateFieldString
            qaAttribute="start-date-input"
            label="Start Date"
            value={formData.startDate}
            onChange={(date) => handleFieldChange('startDate', date || '')}
            error={!!getFieldError('startDateTime')}
            helperText={
              getFieldError('startDateTime') ||
              (scheduleHasStarted ? 'Cannot modify Start Date for started schedules' : undefined)
            }
            required
            disabled={scheduleHasStarted}
          />
        </Grid>
        <Grid item xs={6}>
          <AgentNetDropdownSelector
            qaAttribute="start-time-select"
            label={`Start Time ${getTimeZoneLabel(formData.startDate)}`}
            value={formData.startTime}
            menuOption={(value) => handleFieldChange('startTime', value as string)}
            error={!!getFieldError('startDateTime')}
            required
            isFieldDisable={scheduleHasStarted}
            hideEditable={true}
            options={generateTimeOptions().map((option) => ({ name: option.label, value: option.value }))}
          />
        </Grid>
        <Grid item xs={6}>
          <DateFieldString
            qaAttribute="end-date-input"
            label="End Date"
            value={formData.endDate}
            onChange={(date) => handleFieldChange('endDate', date || '')}
            error={!!getFieldError('endDateTime')}
            helperText={getFieldError('endDateTime')}
          />
        </Grid>
        <Grid item xs={6}>
          <AgentNetDropdownSelector
            qaAttribute="end-time-select"
            label={`End Time ${getTimeZoneLabel(formData.endDate)}`}
            value={formData.endTime}
            menuOption={(value) => handleFieldChange('endTime', (value as string) || '')}
            error={!!getFieldError('endDateTime')}
            options={[
              { name: '', value: '' },
              ...generateTimeOptions().map((option) => ({ name: option.label, value: option.value })),
            ]}
          />
        </Grid>
      </Grid>
    </FormDrawerComponent>
  );
};

export default OracleDowntimeDrawer;
